import React, { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import SectionProcessBg from '../components/SectionProcessBg';
import InnovationNetwork, { nodeIdForRow } from '../components/InnovationNetwork';
import { EASE, staggerContainer } from '../motion/variants';

const ITEMS = [
  {
    n: '01',
    title: 'AI-Powered Color Formulation',
    text: 'Spectrophotometric matching algorithms trained on 10,000+ real-world outcomes cut development from weeks to 72 hours, with full CIELab validation at every stage.',
  },
  {
    n: '02',
    title: 'Nano-Scale Dispersion Technology',
    text: 'Twin-screw nano-compounding achieves pigment uniformity that eliminates gels, specks, and flow marks at source — not downstream on your production line.',
  },
  {
    n: '03',
    title: 'Real-Time In-Line Quality Control',
    text: 'NIR and spectral sensors monitor melt quality, pigment concentration, and IV retention continuously. Every batch ships with a QR-linked digital Certificate of Analysis.',
  },
  {
    n: '04',
    title: 'Recyclability-First Product Design',
    text: 'Every new formulation is tested through simulated mechanical recycling loops. Carriers and pigments are selected for post-consumer performance from day one of development.',
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 36 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
};

const rowReveal = {
  hidden: { opacity: 0, x: -28 },
  visible: (i) => ({
    opacity: 1,
    x: 0,
    transition: { duration: 0.65, delay: 0.15 + i * 0.1, ease: EASE },
  }),
};

export default function InnovationSection() {
  const reduceMotion = useReducedMotion();
  const [activeNode, setActiveNode] = useState(null);

  return (
    <section id="innovation" className="innovation-lab sec-process-wrap" data-palette="navy">
      <SectionProcessBg variant="lab" seed={5} />
      <div className="innovation-lab__ambient" aria-hidden="true">
        <span className="innovation-lab__mesh" />
        <span className="innovation-lab__scan" />
        <span className="innovation-lab__orb innovation-lab__orb--1" />
        <span className="innovation-lab__orb innovation-lab__orb--2" />
      </div>

      <div className="innovation-lab__inner sec-process-content">
        <div className="innovation-lab__grid">
          <motion.div
            className="innovation-lab__copy"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            <motion.div className="innovation-lab__hdr" variants={fadeUp}>
              <div className="kicker k-gold">Innovation Lab</div>
              <h2 className="title lt">
                The science
                <br />
                behind <em>the color</em>
              </h2>
            </motion.div>

            <div className="innovation-lab__items">
              {ITEMS.map((item, i) => (
                <motion.div
                  key={item.n}
                  className="innovation-lab__row"
                  custom={i}
                  variants={rowReveal}
                  onMouseEnter={() => setActiveNode(nodeIdForRow(i))}
                  onMouseLeave={() => setActiveNode(null)}
                  onFocus={() => setActiveNode(nodeIdForRow(i))}
                  onBlur={() => setActiveNode(null)}
                  tabIndex={0}
                  whileHover={reduceMotion ? undefined : { x: 8 }}
                >
                  <span className="innovation-lab__row-bar" aria-hidden="true" />
                  <div className="innovation-lab__n">{item.n}</div>
                  <div>
                    <h4>{item.title}</h4>
                    <p>{item.text}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            className="innovation-lab__viz"
            initial={{ opacity: 0, scale: 0.92 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.2 }}
          >
            <div className="innovation-lab__viz-frame">
              <span className="innovation-lab__viz-tag">Live formulation mesh</span>
              <InnovationNetwork activeNode={activeNode} />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
